import React, { useState, useEffect } from 'react';
import { Grid, Container, Typography, Paper, Card, CardHeader, CardContent, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Map, TileLayer, Polygon, WMSTileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LeafletMap from '../../components/LeafletMap';
import PageTitle from '../../components/PageTitle';
import apiService from '../../services/api';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
});

// Farm Selector Component
function FarmSelector({ farms, currentFarmId, onSelectFarm, isLoading }) {
  const selectorStyles = makeStyles(theme => ({
    formControl: {
      minWidth: 300,
      marginBottom: theme.spacing(3)
    },
    select: {
      '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center'
      }
    },
    loadingSpinner: {
      marginLeft: theme.spacing(1),
      color: theme.palette.primary.main
    }
  }))();

  const handleChange = (event) => {
    const selectedFarm = farms.find(farm => farm.id === event.target.value);
    if (selectedFarm) {
      onSelectFarm(selectedFarm);
    }
  };

  return (
    <FormControl variant="outlined" className={selectorStyles.formControl}>
      <InputLabel id="farm-select-label">Select Farm</InputLabel>
      <Select
        labelId="farm-select-label"
        id="farm-select"
        value={currentFarmId || ''}
        onChange={handleChange}
        label="Select Farm"
        className={selectorStyles.select}
        disabled={isLoading}
        endAdornment={isLoading && <CircularProgress size={20} className={selectorStyles.loadingSpinner} />}
      >
        {farms.map((farm) => (
          <MenuItem key={farm.id} value={farm.id}>
            {farm.name} - {farm.crop_type || 'Unknown Crop'} ({farm.area_hectares ? `${farm.area_hectares.toFixed(2)} ha` : 'Unknown size'})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// Helper function to calculate center of polygon
const calculateCenter = (coordinates) => {
  // Check if we have valid coordinates
  if (!coordinates || !coordinates.length || !coordinates[0] || !coordinates[0].length) {
    console.warn('Invalid coordinates structure received');
    // Default to South Africa center coordinates
    return [-29, 25];
  }

  try {
    // Handle both [lng, lat] and [lat, lng] formats
    const points = coordinates[0];
    let totalLat = 0;
    let totalLng = 0;
    
    // Log the first point for debugging
    console.log('First coordinate point:', points[0]);
    
    // Geojson standard is [lng, lat] for each coordinate
    points.forEach(point => {
      // Make sure we're dealing with numbers
      const lng = parseFloat(point[0]);
      const lat = parseFloat(point[1]);
      
      if (isNaN(lat) || isNaN(lng)) {
        console.warn('Found invalid coordinate point:', point);
        return; // Skip this point
      }
      
      totalLng += lng;
      totalLat += lat;
    });
    
    // Get valid points count
    const validPointsCount = points.filter(p => 
      !isNaN(parseFloat(p[0])) && !isNaN(parseFloat(p[1]))
    ).length;
    
    if (validPointsCount === 0) {
      console.warn('No valid points found in coordinates');
      return [-29, 25]; // Default to South Africa
    }
    
    // Calculate average of valid points
    const centerLat = totalLat / validPointsCount;
    const centerLng = totalLng / validPointsCount;
    
    // Swap coordinates for Leaflet - Leaflet uses [lat, lng]
    console.log(`Calculated center: [${centerLat}, ${centerLng}]`);
    return [centerLat, centerLng];
  } catch (error) {
    console.error('Error calculating center:', error);
    return [-29, 25]; // Default to South Africa
  }
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  mapCard: {
    marginBottom: theme.spacing(3),
    position: 'relative'
  },
  mapContainer: {
    height: 500,
    width: '100%',
    position: 'relative',
    zIndex: 0,
    '& .leaflet-container': {
      height: '100%',
      width: '100%'
    }
  },
  selectorContainer: {
    marginBottom: theme.spacing(3)
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1
  }
}));

const FarmMapPage = () => {
  const classes = useStyles();
  const [farms, setFarms] = useState([]);
  const [currentFarm, setCurrentFarm] = useState(null);
  const [farmGeometry, setFarmGeometry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState([-29, 25]); // Default to South Africa
  const [zoomLevel, setZoomLevel] = useState(6);

  // Fetch farms on component mount
  useEffect(() => {
    const fetchFarms = async () => {
      try {
        setLoading(true);
        const farmsData = await apiService.getFarms();
        
        if (farmsData && farmsData.length > 0) {
          setFarms(farmsData);
          
          // Set the first farm as selected by default
          handleFarmSelect(farmsData[0]);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching farms:', error);
        setLoading(false);
      }
    };

    fetchFarms();
  }, []);

  // Handler for when a farm is selected
  const handleFarmSelect = async (farm) => {
    try {
      setLoading(true);
      setCurrentFarm(farm);
      
      // Fetch farm geometry from API
      const geometry = await apiService.getFarmGeometry(farm.id);
      console.log('Farm geometry from API:', geometry);
      
      if (geometry && geometry.coordinates && geometry.coordinates.length > 0) {
        setFarmGeometry(geometry);
        
        // Now using fitBounds in the LeafletMap component
        // Just set a reasonable zoom level for initial rendering
        setZoomLevel(14);
        
        // Default center used only initially
        // The FitBounds component will adjust the view
        setMapCenter([-29, 25]);
      } else {
        console.warn('Invalid geometry received for farm:', farm.id);
        setFarmGeometry(null);
        setMapCenter([-29, 25]);
        setZoomLevel(6);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching farm data:', error);
      setFarmGeometry(null);
      setMapCenter([-29, 25]);
      setZoomLevel(6);
      setLoading(false);
    }
  };

  // Prepare markers based on current farm
  const farmMarkers = currentFarm ? [
    {
      lat: mapCenter[0], 
      lng: mapCenter[1],
      popupContent: `${currentFarm.name} - ${currentFarm.crop_type || 'Unknown Crop'}`
    }
  ] : [];

  // Extract boundary coordinates directly for use with Polygon component
  const [boundaryCoordinates, setBoundaryCoordinates] = useState([]);
  
  // Process farm geometry into simple polygon coordinates when it changes
  useEffect(() => {
    try {
      console.log('Farm geometry received:', farmGeometry);
      if (farmGeometry && farmGeometry.coordinates) {
        // The API response contains coordinates in [lat, lng] format
        // which is exactly what Leaflet expects for the Polygon component
        
        // Handle nested coordinate arrays from API
        if (Array.isArray(farmGeometry.coordinates[0]) && 
            Array.isArray(farmGeometry.coordinates[0][0])) {
          // If the coordinates are in GeoJSON format [[[]]] (nested arrays)
          console.log('Processing nested coordinates array');
          setBoundaryCoordinates(farmGeometry.coordinates[0]);
        } else {
          // If the coordinates are in simple array format (direct polygon points)
          console.log('Processing simple coordinates array');
          setBoundaryCoordinates(farmGeometry.coordinates);
        }
      } else {
        console.warn('Invalid farm geometry structure');
        setBoundaryCoordinates([]);
      }
    } catch (error) {
      console.error('Error processing farm boundary:', error);
      setBoundaryCoordinates([]);
    }
  }, [farmGeometry]);

  return (
    <div className={classes.root}>
      <PageTitle title="SANSA Powered Farm Maps" />
      
      <Container maxWidth="lg">
        {/* Farm Selector */}
        <div className={classes.selectorContainer}>
          <Typography variant="h6" gutterBottom>
            Select a Farm to View
          </Typography>
          <FarmSelector
            farms={farms}
            currentFarmId={currentFarm ? currentFarm.id : ''}
            onSelectFarm={handleFarmSelect}
            isLoading={loading}
          />
          {currentFarm && (
            <Typography variant="body2" style={{ marginTop: 16 }}>
              <b>Selected Farm:</b> {currentFarm.name} - {currentFarm.crop_type || 'Unknown Crop'} 
              ({currentFarm.area_hectares ? `${currentFarm.area_hectares.toFixed(2)} ha` : 'Unknown size'})
            </Typography>
          )}
        </div>
        
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            NDVI Analysis with Farm Boundary
          </Typography>
          <Typography variant="body2" paragraph>
          This map shows the farm boundary overlaid with SANSA's NDVI difference map for May 20, 2024 (layer: cropwatch:ndvi_difference_map_20240520). The green polygon represents the farm boundary, and the color overlay reflects vegetation health.
            {/* This map shows the farm boundary with SANSA's NDVI difference map (cropwatch:ndvi_difference_map_20240520).
            The green polygon represents the farm boundary, and the color overlay represents vegetation health. */}
          </Typography>
          {loading ? (
            <div style={{ height: 500, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress size={60} />
            </div>
          ) : (
            <LeafletMap 
              height={500}
              title="Farm NDVI Analysis"
              markers={farmMarkers}
              center={mapCenter}
              zoom={zoomLevel}
              polygonPositions={boundaryCoordinates}
              wmsUrl="https://desa.sansa.org.za/ows"
              wmsLayers="cropwatch:ndvi_difference_map_20240520"
              wmsOptions={{
                format: "image/png",
                transparent: true,
                attribution: "Data: SANSA DESA"
              }}
            />
          )}
        </Paper>
        
        <Grid container spacing={3} style={{ marginTop: 16 }}>
          <Grid item xs={12} md={6}>
            <Paper className={classes.paper}>
              <Typography variant="h6" gutterBottom>
                Farm Regional Context
              </Typography>
              {loading ? (
                <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress size={40} />
                </div>
              ) : (
                <LeafletMap 
                  height={400}
                  zoom={12}
                  title="Regional View"
                  center={mapCenter}
                  polygonPositions={boundaryCoordinates}
                />
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper className={classes.paper}>
              <Typography variant="h6" gutterBottom>
                Farm Close-up
              </Typography>
              {loading ? (
                <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress size={40} />
                </div>
              ) : (
                <LeafletMap 
                  height={400}
                  zoom={16}
                  title="Detailed View"
                  center={mapCenter}
                  markers={farmMarkers}
                  polygonPositions={boundaryCoordinates}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
        
        {currentFarm && (
          <Paper className={classes.paper} style={{ marginTop: 16 }}>
            <Typography variant="h6" gutterBottom>
              {currentFarm.name} Details
            </Typography>
            <Typography variant="body1">
              <b>Farm ID:</b> {currentFarm.id}<br />
              <b>Crop Type:</b> {currentFarm.crop_type || 'Not specified'}<br />
              <b>Area:</b> {currentFarm.area_hectares ? `${currentFarm.area_hectares.toFixed(2)} hectares` : 'Unknown'}<br />
              <b>Created:</b> {new Date(currentFarm.created_at).toLocaleDateString()}<br />
              <b>Last Updated:</b> {new Date(currentFarm.updated_at).toLocaleDateString()}
            </Typography>
          </Paper>
        )}
      </Container>
    </div>
  );
};

export default FarmMapPage;
